<script lang="ts" setup>
import { type DispatcherPermissionGroupItem, DispatcherPermissionValue } from "@/api/dto/dispatcher.dto";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { Role } from "@/constants/navigation";

const model = defineModel<DispatcherPermissionValue[]>({ default: [] });

const { t } = useI18n();

const props = defineProps<{
  permissionGroup: DispatcherPermissionGroupItem;
  roles: Role[];
}>();

const groupValues = computed(() => {
  return props.permissionGroup.permissions.map((item) => item.value);
});

const isGroupChecked = computed(() => {
  return groupValues.value.every((permission) => model.value.includes(permission));
});

const isAdmin = computed(() => {
  return props.roles.some((role) => role === Role.admin);
});

function togglePermission(value: DispatcherPermissionValue, checked: boolean) {
  if (checked && !model.value.includes(value)) {
    model.value = [...model.value, value];
  } else {
    model.value = model.value.filter((permission) => permission !== value);
  }
}

function toggleGroup(checked: boolean) {
  if (checked) {
    model.value = Array.from(new Set([...model.value, ...groupValues.value]));
  } else {
    model.value = model.value.filter((permission) => !groupValues.value.includes(permission));
  }
}
</script>

<template>
  <v-card class="dispatcher-card">
    <v-row>
      <div class="d-flex justify-space-between align-center w-100">
        <span class="font-size-11 text-grey-darken-1"> {{ props.permissionGroup.group }}: </span>

        <v-checkbox
          :model-value="isGroupChecked"
          :readonly="isAdmin"
          class="font-size-14"
          color="primary"
          density="compact"
          hide-details
          @update:model-value="(checked) => toggleGroup(checked as boolean)"
        >
          <template #label>
            <span class="font-size-12 text-grey-darken-2 ml-2">{{ t("general.select-all") }}</span>
          </template>
        </v-checkbox>
      </div>

      <div v-for="permission in permissionGroup.permissions" :key="permission.value" class="w-100">
        <v-checkbox
          :model-value="model.includes(permission.value)"
          :readonly="isAdmin"
          class="font-size-14"
          color="primary"
          density="compact"
          hide-details
          @update:model-value="(checked) => togglePermission(permission.value, checked as boolean)"
        >
          <template #label>
            <span class="font-size-12 text-grey-darken-2 ml-2">
              {{ permission.name }}
            </span>
          </template>
        </v-checkbox>
      </div>
    </v-row>
  </v-card>
</template>

<style lang="scss" scoped>
.dispatcher-card {
  padding: 20px 26px 20px 26px;
  min-height: 370px;
}
</style>
